/**
 * `/recruiting/sourcing` — the Sourcing screen, WP-W12 (Phase 6 SPA, consuming the
 * Task 20 sourcing API). `web/shapes/sourcing.md` is the agreed shape, written before
 * this file.
 *
 * The recruiter-facing surface for candidate discovery — and, like Outreach (WP-W11)
 * before it, its subject is not only the runs it lists but the **compliance posture
 * of where those leads came from**: four low-risk channels are the whole of what this
 * screen ever lets a recruiter trigger; the five high-risk ones are shown, always,
 * quarantined and explained, never silently hidden (§1 of the shape).
 *
 * ── Two personas, two gates, one screen ───────────────────────────────────────
 *
 * The menu item's own `requires` is `recruiting.view_candidates` — the floor the
 * route guard checks, and what the run list needs. Triggering a run is different:
 * `recruiting.manage_jobs`, the same code the requisitions surface uses for its
 * writes, because triggering a discovery run is a decision about a requisition's
 * hiring pipeline. `OutreachScreen`'s own split between the always-visible message
 * list and the `workforce.manage_policies`-gated channel-policy panel is the template
 * this screen follows — the trigger panel is rendered only for a session holding the
 * code, never the authorisation, which the server still runs on every call.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type { RecruitingRequisitionListResponse } from '../../api/recruiting';
import { recruitingRequisitionsPath } from '../../api/recruiting';
import type {
  SourcingRun,
  SourcingRunListResponse,
  SourcingRunTriggerRequest,
  SourcingRunTriggerResponse,
} from '../../api/sourcing';
import { sourcingRunsPath, SOURCING_RUNS_PATH } from '../../api/sourcing';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  SOURCING_CHANNEL_LABEL,
  SOURCING_RUN_STATUS_BADGE,
  SOURCING_RUN_STATUS_ICON,
  SOURCING_RUN_STATUS_LABEL,
} from '../../recruiting/sourcing';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { SourcingTriggerPanel } from './SourcingTriggerPanel';
import type { PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const RUNS_LIMIT = 100;

export function SourcingScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const permissions = user?.permissions ?? [];
  const mayTrigger = permissions.includes('recruiting.manage_jobs');

  /* ── Requisitions enrichment — a filter's names, or an honest id fallback ─────── */
  const requisitions = useApiQuery<RecruitingRequisitionListResponse>(
    recruitingRequisitionsPath({ limit: PAGE_LIMIT }),
  );
  const requisitionOptions: readonly PickerOption[] | null = useMemo(() => {
    if (requisitions.status !== 'ok' || requisitions.data === null) return null;
    return requisitions.data.data.map((req) => ({ id: req.id, label: req.title }));
  }, [requisitions.status, requisitions.data]);
  const requisitionTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const req of requisitions.data?.data ?? []) map.set(req.id, req.title);
    return map;
  }, [requisitions.data]);

  /* ── Run list — filtered to one requisition, or company-wide ──────────────────── */
  const [requisitionFilter, setRequisitionFilter] = useState('');
  const [listToken, setListToken] = useState(0);
  const trimmedFilter = requisitionFilter.trim();
  const listPath = sourcingRunsPath({
    requisitionId: trimmedFilter === '' ? undefined : trimmedFilter,
    limit: RUNS_LIMIT,
  });
  const runs = useApiQuery<SourcingRunListResponse>(listPath, listToken);
  const rows: readonly SourcingRun[] = runs.data?.data ?? [];

  /* ── Trigger ────────────────────────────────────────────────────────────────── */
  const trigger = useApiMutation<SourcingRunTriggerRequest, SourcingRunTriggerResponse>(
    SOURCING_RUNS_PATH,
    'POST',
  );
  const submitTrigger = (body: SourcingRunTriggerRequest): void => {
    trigger
      .submit(body)
      .then(() => {
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal is already on screen. */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.sourcing')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('sourcing.intro')}</p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w12-req-filter">
            {t('sourcing.filter.requisition')}
          </label>
          {requisitionOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w12-req-filter"
                data-testid="w12-req-filter"
                type="text"
                value={requisitionFilter}
                autoComplete="off"
                onChange={(event) => {
                  setRequisitionFilter(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('sourcing.filter.requisitionIdHint')}</p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w12-req-filter"
              data-testid="w12-req-filter"
              value={requisitionFilter}
              onChange={(event) => {
                setRequisitionFilter(event.target.value);
              }}
            >
              <option value="">{t('filters.all')}</option>
              {requisitionOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <section className="wp-section" aria-labelledby="w12-list-heading" data-testid="w12-runs">
        <h2 className="wp-section__title" id="w12-list-heading">
          {t('sourcing.runs.title')}
        </h2>

        {runs.status === 'loading' ? (
          <Skeleton
            rows={3}
            variant="block"
            label={t('sourcing.runs.subjectWord')}
            testId="w12-list-skeleton"
          />
        ) : null}
        {runs.status === 'error' && runs.error !== null ? (
          <ApiErrorNote
            error={runs.error}
            onRetry={runs.reload}
            subject={t('sourcing.runs.subjectWord')}
            testId="w12-list-error"
          />
        ) : null}

        {runs.data === null ? null : rows.length === 0 ? (
          <EmptyState
            title={t('sourcing.runs.empty.title')}
            description={t('sourcing.runs.empty.body')}
            testId="w12-list-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w12-list">
            {rows.map((run) => (
              <li className="wp-row" key={run.id} data-testid={`w12-run-${run.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={SOURCING_RUN_STATUS_BADGE[run.status]}
                      icon={SOURCING_RUN_STATUS_ICON[run.status]}
                      label={t(SOURCING_RUN_STATUS_LABEL[run.status])}
                      testId={`w12-run-status-${run.id}`}
                    />{' '}
                    <span data-testid={`w12-run-channel-${run.id}`}>
                      {t(SOURCING_CHANNEL_LABEL[run.channel])}
                    </span>
                  </p>
                  <p className="wp-small wp-muted" data-testid={`w12-run-requisition-${run.id}`}>
                    {run.requisition_id === null
                      ? t('sourcing.runs.row.noRequisition')
                      : (requisitionTitle.get(run.requisition_id) ?? run.requisition_id)}
                  </p>
                  <p className="wp-small wp-muted" data-testid={`w12-run-leads-${run.id}`}>
                    {t('sourcing.runs.row.leads', {
                      found: run.leads_found,
                      leadsNew: run.leads_new,
                    })}
                  </p>
                  {run.error_message === null ? null : (
                    <p className="wp-small wp-muted" data-testid={`w12-run-error-${run.id}`}>
                      {run.error_message}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Starting a run is an action, so it sits under the runs a recruiter came to
          read rather than above them. */}
      {mayTrigger ? (
        <SourcingTriggerPanel
          requisitionOptions={requisitionOptions}
          pending={trigger.status === 'pending'}
          error={trigger.error}
          triggered={trigger.status === 'ok' ? trigger.data : null}
          onTrigger={submitTrigger}
        />
      ) : null}
    </div>
  );
}
